import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
  Container,
  Box,
} from "@mui/material";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";

import Page from "../../components/Page";
import { usePGIMode } from "../../Hooks/PGIModeContext";
import { login, twoFactorVerification } from "src/DAL/AUTH";
import TwoFactorVerificationFormLogin from "./TwoFactorVerificationFormLogin";

const AuthLayout = ({ children }) => {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "primary.light",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ width: "100%" }}>{children}</Box>
      </Container>
    </Box>
  );
};

export default function Login() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { metaTitle } = usePGIMode();

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  });

  const [step, setStep] = useState("login");
  const [accessToken, setAccessToken] = useState("");

  const handleChange = (field) => (event) => {
    setCredentials((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmitLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    const result = await login(credentials);
    if (result.code === 200) {
      setStep("login");
      result.adminUser &&
        localStorage.setItem("user_data", JSON.stringify(result.adminUser));

      result.token && localStorage.setItem("token", result.token);
      result.adminUser?.email &&
        localStorage.setItem("email", result.adminUser.email);

      localStorage.setItem("is_2f_enabled", result.adminUser.is_2f_enabled);

      result.adminUser?.image &&
        localStorage.setItem("image", result.adminUser.image);

      result.adminUser?.is_send_code !== undefined &&
        localStorage.setItem("is_send_code", result.adminUser.is_send_code);

      result.adminUser?.is_verified_code !== undefined &&
        localStorage.setItem(
          "is_verified_code",
          result.adminUser.is_verified_code
        );

      result.adminUser?.verification_code &&
        localStorage.setItem(
          "verification_code",
          result.adminUser.verification_code
        );

      result.adminUser?.name &&
        localStorage.setItem("name", result.adminUser.name);

      result.adminUser?._id &&
        localStorage.setItem("_id", result.adminUser._id);

      result.adminUser?.role &&
        localStorage.setItem("role", result.adminUser.role);

      enqueueSnackbar(result.message, { variant: "success" });
      navigate("/dashboard", { replace: true });
      setLoading(false);
    } else if (result.code === 201) {
      setAccessToken(result.detail_token);
      enqueueSnackbar(result.message, { variant: "success" });
      setLoading(false);
      setStep("2fa");
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setLoading(false);
    }
  };

  const handleVerifyCode = async (data) => {
    setLoading(true);
    const result = await twoFactorVerification(data);
    if (result.code === 200) {
      result.token && localStorage.setItem("token", result.token);
      result.adminUser &&
        localStorage.setItem("user_data", JSON.stringify(result.adminUser));
      result.adminUser?.email &&
        localStorage.setItem("email", result.adminUser.email);
      localStorage.setItem("is_2f_enabled", result.adminUser.is_2f_enabled);
      result.adminUser?.image &&
        localStorage.setItem("image", result.adminUser.image);
      result.adminUser?.is_send_code !== undefined &&
        localStorage.setItem("is_send_code", result.adminUser.is_send_code);
      result.adminUser?.is_verified_code !== undefined &&
        localStorage.setItem(
          "is_verified_code",
          result.adminUser.is_verified_code
        );
      result.adminUser?.verification_code &&
        localStorage.setItem(
          "verification_code",
          result.adminUser.verification_code
        );
      result.adminUser?.name &&
        localStorage.setItem("name", result.adminUser.name);
      result.adminUser?._id &&
        localStorage.setItem("_id", result.adminUser._id);
      result.adminUser?.role &&
        localStorage.setItem("role", result.adminUser.role);
      enqueueSnackbar(result.message, { variant: "success" });
      navigate("/dashboard", { replace: true });
      setLoading(false);
    } else {
      enqueueSnackbar(result.message, { variant: "error" });
      setLoading(false);
    }
  };

  return (
    <Page title={`Login - ${metaTitle}`}>
      <AuthLayout>
        <Card sx={{ width: "100%" }}>
          <CardContent sx={{ p: 4 }}>
            {step === "login" ? (
              <Stack spacing={1}>
                <Typography variant="h3">Admin Login</Typography>
                <Typography variant="body2" color="text.secondary">
                  Enter your credentials to access the admin panel
                </Typography>
              </Stack>
            ) : (
              <Stack spacing={1}>
                <Typography variant="h3">Enter Verification Code</Typography>
                <Typography variant="body2" color="text.secondary">
                  Check your registered email for your Verification Code
                </Typography>
              </Stack>
            )}
            {step === "login" ? (
              <form onSubmit={handleSubmitLogin}>
                <Stack spacing={3} sx={{ mt: 4 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={credentials.email}
                    onChange={handleChange("email")}
                    required
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={credentials.password}
                    onChange={handleChange("password")}
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    disabled={loading}
                  >
                    {loading ? "Signing in..." : "Sign In"}
                  </Button>
                </Stack>
              </form>
            ) : (
              <TwoFactorVerificationFormLogin
                isLoading={loading}
                accessToken={accessToken}
                setStep={setStep}
                onhandleVerify={handleVerifyCode}
              />
            )}
          </CardContent>
        </Card>
      </AuthLayout>
    </Page>
  );
}
